import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Zap, Video, ExternalLink, CheckCircle, Clock, Sparkles, RefreshCw, FileText } from 'lucide-react';
import { API, getHeaders, useBrand } from '../portalShared';

// Icon lookup for amplification channels — keeps the picker visually distinct.
const CHANNEL_TINT = {
  linkedin: 'from-[#0A66C2]/20 to-[#0A66C2]/5 border-[#0A66C2]/30',
  meta:     'from-[#0668E1]/20 to-[#0668E1]/5 border-[#0668E1]/30',
  google:   'from-[#4285F4]/20 to-[#4285F4]/5 border-[#4285F4]/30',
  youtube:  'from-[#FF0000]/20 to-[#FF0000]/5 border-[#FF0000]/30',
  reddit:   'from-[#FF4500]/20 to-[#FF4500]/5 border-[#FF4500]/30',
  x:        'from-white/15 to-white/5 border-white/30',
  beehiiv:  'from-[#FFCE1F]/20 to-[#FFCE1F]/5 border-[#FFCE1F]/30',
  openai:   'from-[#10A37F]/20 to-[#10A37F]/5 border-[#10A37F]/30',
};

const STATUS_TINT = {
  paid:      'text-[#10B981]',
  initiated: 'text-yellow-400',
  expired:   'text-[#6B7280]',
  failed:    'text-[#FF4343]',
};

const FULFIL_TINT = {
  queued:    'bg-white/5 text-[#9CA3AF]',
  in_prod:   'bg-yellow-500/10 text-yellow-400',
  live:      'bg-[#10B981]/10 text-[#10B981]',
  reporting: 'bg-[#FF4343]/10 text-[#FF4343]',
  complete:  'bg-[#10B981]/20 text-[#10B981]',
  cancelled: 'bg-white/5 text-[#6B7280]',
};

function AmplifyPicker({ catalog, campaigns, onCheckout, checkingOut, prefill }) {
  const [channel, setChannel] = useState(prefill?.channel || 'linkedin');
  const [spend, setSpend] = useState(prefill?.spend || 2000);
  const [linkedCampaign, setLinkedCampaign] = useState(prefill?.linked_campaign_id || '');
  const [audience, setAudience] = useState('');
  const [notes, setNotes] = useState('');

  const meta = catalog.channels.find(c => c.key === channel);
  const minSpend = meta?.min_spend_usd || 0;
  const spendNum = parseFloat(spend) || 0;
  const baseFee = catalog.base_fee_usd || 0;
  const mgmtFee = Math.round(spendNum * catalog.mgmt_fee_rate * 100) / 100;
  const total = spendNum + mgmtFee + baseFee;
  const belowMin = spendNum > 0 && spendNum < minSpend;
  const notLive = meta && !meta.live;

  return (
    <div className="space-y-4" data-testid="amplify-picker">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {catalog.channels.map(ch => {
          const selected = channel === ch.key;
          return (
            <button
              key={ch.key}
              type="button"
              onClick={() => setChannel(ch.key)}
              data-testid={`amplify-channel-${ch.key}`}
              className={`text-left p-3 border-2 rounded transition-all bg-gradient-to-br ${CHANNEL_TINT[ch.key] || 'from-white/5 to-white/0 border-white/10'} ${selected ? 'ring-2 ring-[#FF4343] border-[#FF4343]' : ''}`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-semibold text-white">{ch.label}</span>
                {!ch.live && <span className="text-[9px] font-mono uppercase text-yellow-400 bg-yellow-500/10 px-1.5 py-0.5 rounded">Soon</span>}
              </div>
              <p className="text-[10px] text-[#9CA3AF] leading-snug mb-1">{ch.tagline}</p>
              <p className="text-[10px] font-mono text-[#6B7280]">Min ${ch.min_spend_usd.toLocaleString()}</p>
            </button>
          );
        })}
      </div>

      <div className="bg-[#111827] border border-white/10 p-4 sm:p-5 space-y-4">
        <div>
          <label className="text-[10px] text-[#6B7280] uppercase font-mono block mb-1.5">Ad spend budget (USD)</label>
          <div className="flex gap-2">
            {[1000, 2500, 5000, 10000, 25000].map(v => (
              <button key={v} type="button" onClick={() => setSpend(v)}
                data-testid={`amplify-preset-${v}`}
                className={`text-[11px] px-3 py-1.5 border font-mono ${spendNum === v ? 'border-[#FF4343] text-[#FF4343] bg-[#FF4343]/5' : 'border-white/10 text-[#9CA3AF]'}`}>
                ${v.toLocaleString()}
              </button>
            ))}
          </div>
          <input
            type="number" min={minSpend} step="100"
            value={spend} onChange={e => setSpend(e.target.value)}
            data-testid="amplify-spend-input"
            className="w-full mt-2 px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-lg font-mono focus:outline-none focus:border-[#FF4343]"
          />
          {belowMin && <p className="text-[10px] text-[#FF4343] mt-1">Minimum ad spend on {meta.label} is ${minSpend.toLocaleString()}.</p>}
        </div>

        {campaigns?.length > 0 && (
          <div>
            <label className="text-[10px] text-[#6B7280] uppercase font-mono block mb-1.5">Attach to existing campaign (optional)</label>
            <select value={linkedCampaign} onChange={e => setLinkedCampaign(e.target.value)}
              data-testid="amplify-link-campaign"
              className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
              <option value="">— Not attached (standalone) —</option>
              {campaigns.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </div>
        )}

        <div>
          <label className="text-[10px] text-[#6B7280] uppercase font-mono block mb-1.5">Target audience (optional)</label>
          <input value={audience} onChange={e => setAudience(e.target.value)}
            placeholder="e.g. Dealer Principals, US, 10+ employees"
            data-testid="amplify-audience"
            className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
        </div>

        <div>
          <label className="text-[10px] text-[#6B7280] uppercase font-mono block mb-1.5">Notes to ops team</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
            placeholder="Anything you want the media buyer to know…"
            data-testid="amplify-notes"
            className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343] resize-none" />
        </div>

        {/* Breakdown */}
        <div className="border-t border-white/5 pt-4 space-y-1.5 font-mono text-sm">
          <div className="flex justify-between text-[#9CA3AF]">
            <span>Pre-paid ad spend</span><span>${spendNum.toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2})}</span>
          </div>
          <div className="flex justify-between text-[#9CA3AF]">
            <span>Freddy Media base management fee</span>
            <span>${baseFee.toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2})}</span>
          </div>
          <div className="flex justify-between text-[#9CA3AF]">
            <span>Ad-spend management fee ({(catalog.mgmt_fee_rate * 100).toFixed(0)}%)</span>
            <span>${mgmtFee.toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2})}</span>
          </div>
          <div className="flex justify-between text-white font-bold text-base pt-1.5 border-t border-white/10">
            <span>Total charged today</span>
            <span className="text-[#FF4343]" data-testid="amplify-total">
              ${total.toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2})}
            </span>
          </div>
        </div>

        <button
          disabled={belowMin || notLive || checkingOut || spendNum <= 0}
          onClick={() => onCheckout({
            order_type: 'amplify', channel,
            ad_spend_usd: spendNum,
            linked_campaign_id: linkedCampaign || null,
            target_audience: audience, notes,
          })}
          data-testid="amplify-checkout-btn"
          className="w-full border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] py-3 text-sm font-semibold hover:bg-[#E03030] disabled:opacity-40 transition-all">
          {checkingOut ? 'Redirecting to Stripe…' : notLive ? 'Waitlist — coming soon' : `Pay ${total > 0 ? '$'+total.toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2}) : ''} & launch`}
        </button>
        <p className="text-[10px] text-[#6B7280] text-center">Secure checkout via Stripe. Ad spend is pre-paid; unused budget refunded at campaign close.</p>
      </div>
    </div>
  );
}

function ProductionPicker({ catalog, campaigns, onCheckout, checkingOut, category = 'production' }) {
  const packages = (catalog.packages || []).filter(p => (p.category || 'production') === category);
  const [pkg, setPkg] = useState(packages[0]?.key || '');
  const [linkedCampaign, setLinkedCampaign] = useState('');
  const [location, setLocation] = useState('');
  const [dates, setDates] = useState('');
  const [notes, setNotes] = useState('');
  const selected = packages.find(p => p.key === pkg);
  const isProduction = category === 'production';

  return (
    <div className="space-y-4" data-testid={`${category}-picker`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {packages.map(p => {
          const active = pkg === p.key;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => setPkg(p.key)}
              data-testid={`${category}-package-${p.key}`}
              className={`text-left p-4 border-2 rounded transition-all ${active ? 'border-[#FF4343] bg-[#FF4343]/10' : 'border-white/10 bg-white/5 hover:border-white/30'}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="text-sm font-semibold text-white">{p.label}</div>
                  <div className="text-[10px] uppercase font-mono tracking-wider text-[#FF4343] mt-0.5">
                    {p.billing === 'monthly' ? 'Monthly retainer' : 'One-time'}
                  </div>
                </div>
                <span className="font-mono text-lg font-bold text-white">${p.price_usd.toLocaleString()}</span>
              </div>
              <p className="text-[11px] text-[#9CA3AF] mb-2 leading-snug">{p.tagline}</p>
              <ul className="space-y-1">
                {p.includes.map((inc, i) => (
                  <li key={i} className="text-[10px] text-[#6B7280] flex items-start gap-1.5">
                    <CheckCircle className="w-3 h-3 text-[#10B981] flex-shrink-0 mt-0.5" /> {inc}
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>

      <div className="bg-[#111827] border border-white/10 p-4 sm:p-5 space-y-4">
        {campaigns?.length > 0 && (
          <div>
            <label className="text-[10px] text-[#6B7280] uppercase font-mono block mb-1.5">Attach to existing campaign (optional)</label>
            <select value={linkedCampaign} onChange={e => setLinkedCampaign(e.target.value)}
              data-testid={`${category}-link-campaign`}
              className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
              <option value="">— Not attached (standalone) —</option>
              {campaigns.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </div>
        )}
        {isProduction && selected?.billing === 'one_time' && (
          <>
            <div>
              <label className="text-[10px] text-[#6B7280] uppercase font-mono block mb-1.5">Shoot location</label>
              <input value={location} onChange={e => setLocation(e.target.value)}
                placeholder="e.g. Dallas, TX (your HQ)"
                data-testid="production-location"
                className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
            </div>
            <div>
              <label className="text-[10px] text-[#6B7280] uppercase font-mono block mb-1.5">Preferred dates</label>
              <input value={dates} onChange={e => setDates(e.target.value)}
                placeholder="e.g. Week of Feb 15, flexible"
                data-testid="production-dates"
                className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
            </div>
          </>
        )}
        <div>
          <label className="text-[10px] text-[#6B7280] uppercase font-mono block mb-1.5">
            Notes to {isProduction ? 'production' : 'editorial'} team
          </label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
            placeholder={isProduction ? "Talent details, style references, must-have shots…" : "Topic ideas, voice/tone, target keywords, must-mention products…"}
            data-testid={`${category}-notes`}
            className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343] resize-none" />
        </div>

        <div className="border-t border-white/5 pt-4 flex items-center justify-between font-mono">
          <div>
            <div className="text-[10px] text-[#6B7280] uppercase">Total {selected?.billing === 'monthly' ? '/ month' : ''}</div>
            <div className="text-2xl font-bold text-[#FF4343]" data-testid={`${category}-total`}>
              ${(selected?.price_usd || 0).toLocaleString()}
            </div>
          </div>
          <button
            disabled={checkingOut || !selected}
            onClick={() => onCheckout({
              order_type: 'production', package: pkg,
              linked_campaign_id: linkedCampaign || null,
              shoot_location: location, preferred_dates: dates, notes,
            })}
            data-testid={`${category}-checkout-btn`}
            className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-6 py-3 text-sm font-semibold hover:bg-[#E03030] disabled:opacity-40 transition-all">
            {checkingOut ? 'Redirecting…' : 'Pay & book'}
          </button>
        </div>
        <p className="text-[10px] text-[#6B7280] text-center">
          {selected?.billing === 'monthly' ? 'Recurring monthly charge — cancel anytime with 30-day notice.' : 'Secure one-time payment via Stripe.'}
        </p>
      </div>
    </div>
  );
}

function ServiceOrderRow({ order, onChange }) {
  const [cancelling, setCancelling] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isRecurring = order.billing_cycle === 'monthly';
  const subStatus = order.subscription_status; // active | cancel_scheduled | canceled | undefined
  const cancelsAt = order.subscription_cancels_at
    ? new Date(order.subscription_cancels_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  const cancelSubscription = async () => {
    setCancelling(true);
    try {
      await axios.post(
        `${API}/portal/services/orders/${order.id}/cancel-subscription`,
        { at_period_end: true },
        { headers: getHeaders() },
      );
      toast.success('Retainer set to cancel at period end. You keep the service through the paid month.');
      setConfirmOpen(false);
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed — try again or contact ops.');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="bg-[#111827] border border-white/10 p-4" data-testid={`service-order-${order.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-[10px] font-mono uppercase tracking-wider text-[#FF4343]">{order.order_type}</span>
            <span className="text-sm font-semibold text-white">
              {order.channel_label || order.package_label}
            </span>
            {isRecurring && (
              <span className="text-[9px] font-mono uppercase text-[#9CA3AF] bg-white/5 px-1.5 py-0.5 rounded flex items-center gap-1">
                <RefreshCw className="w-2.5 h-2.5" /> Monthly
              </span>
            )}
          </div>
          <p className="text-[10px] text-[#6B7280] font-mono">Order {order.id} · {new Date(order.created_at).toLocaleString()}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="font-mono text-lg font-bold text-white">
            ${(order.total_amount_usd || 0).toLocaleString()}{isRecurring && <span className="text-[10px] text-[#6B7280] font-normal">/mo</span>}
          </p>
          <p className={`text-[10px] font-mono uppercase ${STATUS_TINT[order.payment_status] || 'text-[#6B7280]'}`}>{order.payment_status}</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${FULFIL_TINT[order.fulfillment_status] || 'bg-white/5 text-[#9CA3AF]'}`}>
          {(order.fulfillment_status || 'queued').replace('_', ' ')}
        </span>
        {isRecurring && subStatus === 'active' && (
          <span className="text-[10px] text-[#10B981] font-mono uppercase" data-testid={`sub-status-${order.id}`}>subscription active</span>
        )}
        {isRecurring && subStatus === 'cancel_scheduled' && (
          <span className="text-[10px] text-yellow-400 font-mono uppercase" data-testid={`sub-status-${order.id}`}>
            cancels {cancelsAt || 'at period end'}
          </span>
        )}
        {isRecurring && subStatus === 'canceled' && (
          <span className="text-[10px] text-[#6B7280] font-mono uppercase" data-testid={`sub-status-${order.id}`}>subscription canceled</span>
        )}
        {order.linked_campaign_id && (
          <span className="text-[10px] text-[#9CA3AF]">↳ campaign {order.linked_campaign_id.slice(0, 10)}…</span>
        )}
        {order.asana_link && (
          <a href={order.asana_link} target="_blank" rel="noopener noreferrer"
            className="text-[10px] text-[#FF4343] hover:underline flex items-center gap-1">
            Asana <ExternalLink className="w-3 h-3" />
          </a>
        )}
        {/* Cancel action — only for paid, recurring, still-active retainers */}
        {isRecurring && order.payment_status === 'paid' && subStatus === 'active' && (
          <button
            onClick={() => setConfirmOpen(true)}
            data-testid={`cancel-sub-${order.id}`}
            className="ml-auto text-[10px] font-mono uppercase text-[#FF4343] hover:text-white border border-[#FF4343]/40 px-2 py-1 hover:bg-[#FF4343]/10 transition-colors">
            Cancel retainer
          </button>
        )}
      </div>

      {/* Confirmation modal */}
      {confirmOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => !cancelling && setConfirmOpen(false)}>
          <div className="bg-[#111827] border border-white/10 p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid={`cancel-modal-${order.id}`}>
            <h4 className="font-display text-lg font-bold text-white mb-2">Cancel this retainer?</h4>
            <p className="text-sm text-[#9CA3AF] mb-4 leading-relaxed">
              You&#39;ll keep <span className="text-white">{order.package_label}</span> through the end of the current paid period,
              then it stops billing. No refunds, no partial charges. Reactivating later means starting a fresh order.
            </p>
            <div className="bg-yellow-500/10 border border-yellow-500/30 px-3 py-2 mb-4 text-xs text-yellow-400">
              This action can&#39;t be undone through the portal. Once your subscription ends you&#39;d need to book a new retainer.
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmOpen(false)} disabled={cancelling}
                className="text-xs px-4 py-2 border border-white/20 text-white hover:bg-white/5">
                Keep retainer
              </button>
              <button onClick={cancelSubscription} disabled={cancelling}
                data-testid={`confirm-cancel-${order.id}`}
                className="text-xs px-4 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold hover:bg-[#E03030] disabled:opacity-50">
                {cancelling ? 'Cancelling…' : 'Yes, cancel at period end'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default function BrandServices() {
  const brand = useBrand();
  const [catalog, setCatalog] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState('amplify');
  const [checkingOut, setCheckingOut] = useState(false);
  // Consumed by AmplifyPicker via a prop; stripped from the URL after read
  // so a refresh doesn't accidentally re-open the same pre-fill.
  const [prefill, setPrefill] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    if (!p.get('channel') && !p.get('spend')) return null;
    return {
      channel: p.get('channel') || null,
      spend: p.get('spend') ? parseFloat(p.get('spend')) : null,
      linked_campaign_id: p.get('linked_campaign_id') || null,
    };
  });

  const fetchAll = () => {
    axios.get(`${API}/services/catalog`).then(r => setCatalog(r.data)).catch(() => {});
    axios.get(`${API}/portal/campaigns`, { headers: getHeaders() }).then(r => setCampaigns(r.data || [])).catch(() => {});
    axios.get(`${API}/portal/services/orders`, { headers: getHeaders() }).then(r => setOrders(r.data.orders || [])).catch(() => {});
  };

  useEffect(() => { fetchAll(); }, []);

  // If we deep-linked in with a prefill, strip the URL so a refresh doesn't
  // re-open the same context and toast a friendly nudge.
  useEffect(() => {
    if (!prefill) return;
    toast.info('Pre-filled from your campaign — adjust the spend and check out when you\'re ready.');
    const url = new URL(window.location.href);
    ['channel', 'spend', 'linked_campaign_id'].forEach(k => url.searchParams.delete(k));
    // Preserve ?tab=services so BrandPortal keeps this tab active on hard-refresh.
    if (!url.searchParams.get('tab')) url.searchParams.set('tab', 'services');
    window.history.replaceState({}, '', url.toString());
    // Only fires once — after this we're happy for state to drive everything.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If we came back from Stripe, poll the order status
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get('order_id');
    const sessionId = params.get('session_id');
    if (!orderId || !sessionId) return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/portal/services/orders/${orderId}/status`, { headers: getHeaders() });
        if (data.payment_status === 'paid') {
          toast.success('Payment confirmed — your ops team has been notified.');
          fetchAll();
          // Strip query params so we don't re-poll on refresh
          window.history.replaceState({}, '', `${window.location.pathname}?tab=services`);
          return;
        }
      } catch (_e) { /* ignore transient */ }
      if (attempts < 10) setTimeout(poll, 2000);
    };
    poll();
  }, []);

  const handleCheckout = async (payload) => {
    setCheckingOut(true);
    try {
      const endpoint = payload.order_type === 'amplify'
        ? `${API}/portal/services/checkout/amplify`
        : `${API}/portal/services/checkout/production`;
      const { data } = await axios.post(endpoint, {
        ...payload,
        origin_url: window.location.origin,
      }, { headers: getHeaders() });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
      setCheckingOut(false);
    }
  };

  if (!catalog) return <p className="text-[#9CA3AF]">Loading services…</p>;

  return (
    <div className="space-y-6" data-testid="brand-services">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#FF4343]/15 via-[#FF4343]/5 to-[#111827] border border-[#FF4343]/30 p-5">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-[#FF4343]" />
          <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#FF4343]">Managed Services by Freddy Media</p>
        </div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-2">
          Amplify + Produce — <span className="text-[#FF4343]">done for you</span>
        </h2>
        <p className="text-sm text-[#9CA3AF] max-w-2xl leading-relaxed">
          Now that you've got executive endorsements in the tank, get them in front of buyers. Our media team runs the ads across every major B2B channel, and our production crew shoots + edits UGC on-site. All fulfilled by Freddy Media — you approve, we execute.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 overflow-x-auto">
        <button onClick={() => setTab('amplify')} data-testid="services-subtab-amplify"
          className={`px-4 py-2 text-sm font-medium border-b-2 flex items-center gap-2 whitespace-nowrap ${tab === 'amplify' ? 'text-[#FF4343] border-[#FF4343]' : 'text-[#9CA3AF] border-transparent hover:text-white'}`}>
          <Zap className="w-4 h-4" /> Amplify (Ads)
        </button>
        <button onClick={() => setTab('production')} data-testid="services-subtab-production"
          className={`px-4 py-2 text-sm font-medium border-b-2 flex items-center gap-2 whitespace-nowrap ${tab === 'production' ? 'text-[#FF4343] border-[#FF4343]' : 'text-[#9CA3AF] border-transparent hover:text-white'}`}>
          <Video className="w-4 h-4" /> Production (Video/UGC)
        </button>
        <button onClick={() => setTab('writing')} data-testid="services-subtab-writing"
          className={`px-4 py-2 text-sm font-medium border-b-2 flex items-center gap-2 whitespace-nowrap ${tab === 'writing' ? 'text-[#FF4343] border-[#FF4343]' : 'text-[#9CA3AF] border-transparent hover:text-white'}`}>
          <FileText className="w-4 h-4" /> Writing (Blogs/Copy)
        </button>
        <button onClick={() => setTab('orders')} data-testid="services-subtab-orders"
          className={`px-4 py-2 text-sm font-medium border-b-2 flex items-center gap-2 whitespace-nowrap ${tab === 'orders' ? 'text-[#FF4343] border-[#FF4343]' : 'text-[#9CA3AF] border-transparent hover:text-white'}`}>
          <Clock className="w-4 h-4" /> My Orders {orders.length > 0 && <span className="text-[10px] bg-white/10 px-1.5 rounded">{orders.length}</span>}
        </button>
      </div>

      {tab === 'amplify' && (
        <AmplifyPicker catalog={catalog.amplify} campaigns={campaigns}
          onCheckout={handleCheckout} checkingOut={checkingOut} prefill={prefill} />
      )}
      {tab === 'production' && (
        <ProductionPicker catalog={catalog.production} campaigns={campaigns}
          onCheckout={handleCheckout} checkingOut={checkingOut} category="production" />
      )}
      {tab === 'writing' && (
        <ProductionPicker catalog={catalog.production} campaigns={campaigns}
          onCheckout={handleCheckout} checkingOut={checkingOut} category="writing" />
      )}

      {tab === 'orders' && (
        <div className="space-y-3" data-testid="services-orders-list">
          {orders.length === 0 ? (
            <div className="text-center py-10 bg-[#111827] border border-white/10">
              <p className="text-sm text-[#9CA3AF]">No service orders yet — spin one up from Amplify or Production above.</p>
            </div>
          ) : orders.map(o => (
            <ServiceOrderRow key={o.id} order={o} onChange={fetchAll} />
          ))}
        </div>
      )}
    </div>
  );
}
